import React, { useState, useEffect } from "react";
import { submitFlightResponse } from "apis/requestServices";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { ModalProps } from "./Types";
import { useToastStore } from "store/toastStore";
import { ExclamationCircleIcon } from "@heroicons/react/20/solid";

const RequestReplyTab: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  logoImg,
  airlineName,
  flightNumber,
  flightDate,
  airlineId,
  departureAirport,
  arrivalAirport,
  departureTime,
  arrivalTime,
  flightDuration,
  requestDetailId,
}) => {
  const defaultSeatData = {
    requestDetailId: 0,
    firstClass: 0,
    businessClass: 0,
    economyPlusClass: 0,
    economyClass: 0,
    nonRevListed: 0,
    nonFirst: 0,
    nonBusiness: 0,
    nonEconomyPlus: 0,
    nonEconomy: 0,
    moreFirstClass: false,
    moreBusinessClass: false,
    moreEcoClass: false,
    moreEcoPlus: false,
    comments: "",
  };

  const [seatData, setSeatData] = useState(defaultSeatData);
  const [error, setError] = useState<string | null>(null);
  const [focusedClass, setFocusedClass] = useState<
    "First" | "Economy" | "Eco+" | "Business" | "NonRev"
  >("First");
  const [noClassChecked, setNoClassChecked] = useState({
    First: false,
    Business: false,
    Economy: false,
    "Eco+": false,
  });
  const [unknownNonRevCount, setUnknownNonRevCount] = useState(false);
  const [nonRevValidationError, setNonRevValidationError] = useState<
    string | null
  >(null);
  const [dontKnowNonRev, setDontKnowNonRev] = useState(false);

  const { showLoader, hideLoader } = useGlobalLoader();
  const [comment, setComment] = useState("");

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSeatData({
        ...defaultSeatData,
        requestDetailId: requestDetailId || 0,
      });
      setError(null);
      setNonRevValidationError(null);
      setFocusedClass("First");
      setNoClassChecked({
        First: false,
        Business: false,
        Economy: false,
        "Eco+": false,
      });
      setUnknownNonRevCount(false);
    }
  }, [isOpen, requestDetailId]);
  const handleInputChange = (
    field: string,
    value: number | boolean | string
  ) => {
    // Clear non-rev validation error if any of these fields are changed
    if (
      field === "nonRevListed" ||
      field === "nonFirst" ||
      field === "nonBusiness" ||
      field === "nonEconomyPlus" ||
      field === "nonEconomy"
    ) {
      setNonRevValidationError(null);
    }

    // Handle "NoClass" checkbox logic
    if (field === "NoClass") {
      const seatField =
        focusedClass === "First"
          ? "firstClass"
          : focusedClass === "Business"
            ? "businessClass"
            : focusedClass === "Eco+"
              ? "economyPlusClass"
              : "economyClass";
      const moreField =
        focusedClass === "First"
          ? "moreFirstClass"
          : focusedClass === "Business"
            ? "moreBusinessClass"
            : focusedClass === "Eco+"
              ? "moreEcoPlus"
              : "moreEcoClass";

      let nonRevField = "";
      if (focusedClass === "First") nonRevField = "nonFirst";
      else if (focusedClass === "Business") nonRevField = "nonBusiness";
      else if (focusedClass === "Eco+") nonRevField = "nonEconomyPlus";
      else if (focusedClass === "Economy") nonRevField = "nonEconomy";

      setNoClassChecked((prev) => ({
        ...prev,
        [focusedClass]: true,
      }));

      setSeatData((prev) => ({
        ...prev,
        [seatField]: 0,
        [moreField]: false,
        ...(nonRevField && { [nonRevField]: 0 }),
      }));
    }
    // Handle unknownNonRevCount toggle
    else if (field === "unknownNonRevCount") {
      const isChecked = value as boolean;
      setNonRevValidationError(null);
      setUnknownNonRevCount(isChecked);
      if (isChecked) {
        setSeatData((prev) => ({
          ...prev,
          nonFirst: 0,
          nonBusiness: 0,
          nonEconomyPlus: 0,
          nonEconomy: 0,
          nonRevListed: 0,
        }));
      }
    }
    // Handle changes to per-class or non-rev fields
    else {
      if (
        (field === "firstClass" && focusedClass === "First") ||
        (field === "businessClass" && focusedClass === "Business") ||
        (field === "economyPlusClass" && focusedClass === "Eco+") ||
        (field === "economyClass" && focusedClass === "Economy")
      ) {
        if (value !== null) {
          setNoClassChecked((prev) => ({
            ...prev,
            [focusedClass]: false,
          }));
        }
        if (value === "" || value === 0) {
          const nonRevField =
            field === "firstClass"
              ? "nonFirst"
              : field === "businessClass"
                ? "nonBusiness"
                : field === "economyPlusClass"
                  ? "nonEconomyPlus"
                  : "nonEconomy";
          setSeatData((prev) => ({
            ...prev,
            [field]: value,
            [nonRevField]: 0,
          }));
          return;
        }
      }

      // Auto-calculate nonRevListed if any per-class non-rev field changes
      if (
        field === "nonFirst" ||
        field === "nonBusiness" ||
        field === "nonEconomyPlus" ||
        field === "nonEconomy"
      ) {
        const updatedSeatData = {
          ...seatData,
          [field]: Number(value),
        };

        const hasAnyNonRevClassValue =
          updatedSeatData.nonFirst > 0 ||
          updatedSeatData.nonBusiness > 0 ||
          updatedSeatData.nonEconomyPlus > 0 ||
          updatedSeatData.nonEconomy > 0;

        if (!unknownNonRevCount && hasAnyNonRevClassValue) {
          const total =
            Number(updatedSeatData.nonFirst || 0) +
            Number(updatedSeatData.nonBusiness || 0) +
            Number(updatedSeatData.nonEconomyPlus || 0) +
            Number(updatedSeatData.nonEconomy || 0);

          updatedSeatData.nonRevListed = total;
        }

        setSeatData(updatedSeatData);
        return;
      }

      // Default behavior for all other fields
      setSeatData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleClose = () => {
    setSeatData(defaultSeatData);
    setError(null);
    onClose?.();
  };

  const handleSubmit = async () => {
    console.log(seatData);
    // Reset errors
    setError(null);
    setNonRevValidationError(null);

    // Only validate Non-Rev Listed if toggle is OFF
    if (!unknownNonRevCount) {
      const {
        nonRevListed,
        nonFirst,
        nonBusiness,
        nonEconomyPlus,
        nonEconomy,
      } = seatData;

      const hasNonRevFields =
        (nonFirst || 0) > 0 ||
        (nonBusiness || 0) > 0 ||
        (nonEconomyPlus || 0) > 0 ||
        (nonEconomy || 0) > 0;

      const totalNonRevPerClass =
        (nonFirst || 0) +
        (nonBusiness || 0) +
        (nonEconomyPlus || 0) +
        (nonEconomy || 0);

      // Case 1: Any per-class field is filled — enforce match with nonRevListed
      if (hasNonRevFields) {
        if (!nonRevListed || nonRevListed < 0) {
          setNonRevValidationError(
            "Please provide the number of listed Non-Rev passengers."
          );
          return;
        }

        if (totalNonRevPerClass !== nonRevListed) {
          setNonRevValidationError(
            "Total of 'Listed Non-Rev Pax Per Class' must match the 'Non Rev' value."
          );
          return;
        }
      }
    }

    showLoader();
    try {
      await submitFlightResponse({ ...seatData, requestDetailId, comments: comment });
      setComment("");

      setSeatData(defaultSeatData);
      onSubmit?.();
      // Hide loader before showing success toast
      hideLoader();
      useToastStore
        .getState()
        .showToast("Request answered successfully!", "success", 3000);
    } catch (error: any) {
      // Hide loader before showing error toast
      hideLoader();
      useToastStore
        .getState()
        .showToast(error?.response?.data?.message, "error", 3000);
    }
  };

  const getInputValue = (
    classType: "First" | "Economy" | "Eco+" | "Business"
  ) => {
    const isNoClass = noClassChecked[classType];
    const value =
      classType === "First"
        ? seatData.firstClass
        : classType === "Business"
          ? seatData.businessClass
          : classType === "Eco+"
            ? seatData.economyPlusClass
            : seatData.economyClass;
    if (isNoClass) return "";
    return value === 0 ? "" : value;
  };

  const showToggleOptions = focusedClass !== "NonRev";

  if (!isOpen) return null;

  const classInputStyle = (
    value: number,
    classType: "First" | "Economy" | "Eco+" | "Business"
  ) => {
    const isNoClass = noClassChecked[classType];
    return `w-full text-center h-12 border rounded-full text-base font-semibold placeholder:text-sm placeholder:font-normal focus:outline-none focus:ring-2 focus:ring-[#FBBF24] transition-all ${
      value < 0
        ? "border-red-400 bg-red-50 text-red-600"
        : "border-gray-200 text-gray-800"
    } ${isNoClass ? "opacity-40 bg-gray-100" : "bg-white"}`;
  };

  const nonRevClassInputStyle = (isEnabled: boolean) =>
    `w-full text-center h-12 border rounded-full text-base font-semibold placeholder:text-sm placeholder:font-normal focus:outline-none focus:ring-2 focus:ring-[#FBBF24] transition-all ${
      isEnabled && !unknownNonRevCount
        ? "border-gray-200 text-gray-800 bg-white"
        : "border-gray-100 bg-gray-50 text-gray-300"
    }`;

  const toggleStyle =
    "relative w-10 h-5 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#FBBF24] flex-shrink-0";

  return (
    <div
      id="default-modal"
      tabIndex={-1}
      aria-hidden={!isOpen}
      className="fixed inset-0 z-50 flex justify-end items-stretch bg-black/50"
    >
      <div className="relative w-full max-w-md h-full">
        <div className="bg-white shadow-xl h-full overflow-y-auto">
          {/* Header */}
          <div className="flex items-center gap-3 px-5 pt-5 pb-3">
            <button
              type="button"
              onClick={handleClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            >
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h2 className="text-lg font-semibold text-gray-900">Submit Open Request</h2>
          </div>

          <div className="px-5 pb-5">
            {/* Airline Card */}
            <div className="flex items-center gap-3 border border-gray-100 rounded-xl px-4 py-3 mb-4">
              <img className="w-8 h-8 object-contain rounded" src={logoImg} alt={airlineName} />
              <span className="text-sm font-medium text-gray-800">{airlineName}</span>
            </div>

            {/* Flight Route Card */}
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between">
                {/* Departure */}
                <div className="text-left">
                  <div className="flex items-center gap-1 text-xs text-gray-400 mb-1">
                    <i className="st-arrow-slant text-gray-400 text-[10px]"></i>
                    <span>{departureTime}</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{departureAirport}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{departureAirport}</p>
                </div>

                {/* Route Line */}
                <div className="flex-1 flex flex-col items-center mx-4">
                  <div className="flex items-center w-full">
                    <div className="w-2 h-2 rounded-full bg-gray-300 flex-shrink-0"></div>
                    <div className="flex-1 border-b border-dashed border-gray-300"></div>
                    <div className="bg-gray-200 rounded-full p-1.5 mx-1">
                      <i className="st-flight-slim text-gray-500 text-sm"></i>
                    </div>
                    <div className="flex-1 border-b border-dashed border-gray-300"></div>
                    <div className="w-2 h-2 rounded-full bg-gray-300 flex-shrink-0"></div>
                  </div>
                  <span className="text-[10px] text-gray-400 mt-1.5">{flightDuration}</span>
                </div>

                {/* Arrival */}
                <div className="text-right">
                  <div className="flex items-center justify-end gap-1 text-xs text-gray-400 mb-1">
                    <i className="st-arrow-slant text-gray-400 text-[10px] rotate-90"></i>
                    <span>{arrivalTime}</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{arrivalAirport}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{arrivalAirport}</p>
                </div>
              </div>
            </div>

            {/* Date & Flight Number */}
            <div className="flex items-center justify-between px-1 mb-5">
              <div>
                <p className="text-[10px] text-gray-400 uppercase tracking-wide">Date</p>
                <p className="text-sm font-medium text-gray-800 mt-0.5">{flightDate}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide">Flight no</p>
                <p className="text-sm font-medium text-gray-800 mt-0.5">{flightNumber}</p>
              </div>
            </div>

            {/* Class Inputs */}
            <div className="grid grid-cols-4 gap-3 mb-6">
              <input
                type="number"
                placeholder="First"
                onFocus={() => setFocusedClass("First")}
                value={getInputValue("First")}
                onChange={(e) =>
                  handleInputChange(
                    "firstClass",
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                className={classInputStyle(seatData.firstClass, "First")}
              />
              <input
                type="number"
                placeholder="Eco"
                onFocus={() => setFocusedClass("Economy")}
                value={getInputValue("Economy")}
                onChange={(e) =>
                  handleInputChange(
                    "economyClass",
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                className={classInputStyle(seatData.economyClass, "Economy")}
              />
              <input
                type="number"
                placeholder="Eco+"
                onFocus={() => setFocusedClass("Eco+")}
                value={getInputValue("Eco+")}
                onChange={(e) =>
                  handleInputChange(
                    "economyPlusClass",
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                className={classInputStyle(seatData.economyPlusClass, "Eco+")}
              />
              <input
                type="number"
                placeholder="Bus"
                onFocus={() => setFocusedClass("Business")}
                value={getInputValue("Business")}
                onChange={(e) =>
                  handleInputChange(
                    "businessClass",
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                className={classInputStyle(seatData.businessClass, "Business")}
              />
            </div>
            {error && <p className="text-red-500 text-xs mb-3 text-center">{error}</p>}

            {/* Listed Non-Rev Pax Per Class */}
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-700 mb-3 text-center">
                Listed Non-Rev Pax Per Class
              </p>
              <div className="grid grid-cols-4 gap-3">
                <input
                  type="number"
                  min="0"
                  placeholder="First"
                  value={seatData.nonFirst === 0 ? "" : seatData.nonFirst}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    if (/^\d*$/.test(inputValue)) {
                      const numValue = parseInt(inputValue, 10);
                      handleInputChange(
                        "nonFirst",
                        inputValue === "" ? "" : numValue >= 0 ? numValue : 0
                      );
                    }
                  }}
                  className={nonRevClassInputStyle(seatData.firstClass !== 0)}
                  disabled={!seatData.firstClass || unknownNonRevCount}
                />
                <input
                  type="number"
                  min="0"
                  placeholder="Eco"
                  value={seatData.nonEconomy === 0 ? "" : seatData.nonEconomy}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    if (/^\d*$/.test(inputValue)) {
                      const numValue = parseInt(inputValue, 10);
                      handleInputChange(
                        "nonEconomy",
                        inputValue === "" ? "" : numValue >= 0 ? numValue : 0
                      );
                    }
                  }}
                  className={nonRevClassInputStyle(seatData.economyClass !== 0)}
                  disabled={!seatData.economyClass || unknownNonRevCount}
                />
                <input
                  type="number"
                  min="0"
                  placeholder="Eco+"
                  value={seatData.nonEconomyPlus === 0 ? "" : seatData.nonEconomyPlus}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    if (/^\d*$/.test(inputValue)) {
                      const numValue = parseInt(inputValue, 10);
                      handleInputChange(
                        "nonEconomyPlus",
                        inputValue === "" ? "" : numValue >= 0 ? numValue : 0
                      );
                    }
                  }}
                  className={nonRevClassInputStyle(seatData.economyPlusClass !== 0)}
                  disabled={!seatData.economyPlusClass || unknownNonRevCount}
                />
                <input
                  type="number"
                  min="0"
                  placeholder="Bus"
                  value={seatData.nonBusiness === 0 ? "" : seatData.nonBusiness}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    if (/^\d*$/.test(inputValue)) {
                      const numValue = parseInt(inputValue, 10);
                      handleInputChange(
                        "nonBusiness",
                        inputValue === "" ? "" : numValue >= 0 ? numValue : 0
                      );
                    }
                  }}
                  className={nonRevClassInputStyle(seatData.businessClass !== 0)}
                  disabled={!seatData.businessClass || unknownNonRevCount}
                />
              </div>
            </div>

            {/* Listed Passengers */}
            <div className="flex flex-col items-center mb-6">
              <p className="text-sm font-medium text-gray-700 mb-3">Listed Passengers</p>
              <input
                type="number"
                min="0"
                placeholder="Non Rev"
                onFocus={() => setFocusedClass("NonRev")}
                value={seatData.nonRevListed || ""}
                onChange={(e) => {
                  const numValue = Number(e.target.value);
                  if (unknownNonRevCount) {
                    setUnknownNonRevCount(false);
                  }
                  if (numValue >= 0 || e.target.value === "") {
                    handleInputChange("nonRevListed", numValue);
                  }
                }}
                className={`w-28 text-center h-12 border rounded-full text-base font-semibold placeholder:text-sm placeholder:font-normal focus:outline-none focus:ring-2 focus:ring-[#FBBF24] transition-all ${
                  unknownNonRevCount
                    ? "bg-gray-50 border-gray-100 text-gray-300"
                    : "border-gray-200 text-gray-800 bg-white"
                }`}
                disabled={dontKnowNonRev}
              />
              {nonRevValidationError && (
                <div className="mt-2 flex items-center gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <ExclamationCircleIcon className="h-4 w-4 text-yellow-600 flex-shrink-0" />
                  <p className="text-red-600 text-xs">{nonRevValidationError}</p>
                </div>
              )}
            </div>

            {/* Toggle Options */}
            <div className="space-y-1 mb-6">
              {/* Don't know non-rev count */}
              <label className="flex items-center gap-3 py-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={unknownNonRevCount}
                  onChange={(e) =>
                    handleInputChange("unknownNonRevCount", e.target.checked)
                  }
                  onClick={() => {
                    setDontKnowNonRev((prev) => !prev);
                  }}
                />
                <div className={toggleStyle}></div>
                <span className="text-sm text-gray-500">
                  I don't know how many non-rev passengers are listed
                </span>
              </label>

              {showToggleOptions && (
                <>
                  {/* Overbooked */}
                  <label className="flex items-center gap-3 py-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={
                        (focusedClass === "First"
                          ? seatData.firstClass
                          : focusedClass === "Business"
                            ? seatData.businessClass
                            : focusedClass === "Eco+"
                              ? seatData.economyPlusClass
                              : seatData.economyClass) < 0
                      }
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        const seatField =
                          focusedClass === "First"
                            ? "firstClass"
                            : focusedClass === "Business"
                              ? "businessClass"
                              : focusedClass === "Eco+"
                                ? "economyPlusClass"
                                : "economyClass";
                        handleInputChange(seatField, isChecked ? -1 : 0);
                      }}
                      disabled={
                        focusedClass === "First"
                          ? seatData.firstClass === null
                          : focusedClass === "Business"
                            ? seatData.businessClass === null
                            : focusedClass === "Eco+"
                              ? seatData.economyPlusClass === null
                              : seatData.economyClass === null
                      }
                    />
                    <div className={toggleStyle}></div>
                    <span className="text-sm text-gray-500">Overbooked</span>
                  </label>

                  {/* No class */}
                  <label className="flex items-center gap-3 py-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      id="no-class-checkbox"
                      checked={noClassChecked[focusedClass]}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        setNoClassChecked((prev) => ({
                          ...prev,
                          [focusedClass]: isChecked,
                        }));
                        if (isChecked) {
                          handleInputChange("NoClass", true);
                        } else {
                          const seatField =
                            focusedClass === "First"
                              ? "firstClass"
                              : focusedClass === "Business"
                                ? "businessClass"
                                : focusedClass === "Eco+"
                                  ? "economyPlusClass"
                                  : "economyClass";
                          handleInputChange(seatField, "");
                        }
                      }}
                    />
                    <div className={toggleStyle}></div>
                    <span className="text-sm text-gray-500">
                      This flight has no {focusedClass} class
                    </span>
                  </label>

                  {/* More seats available */}
                  <label
                    className={`flex items-start gap-3 py-3 ${noClassChecked[focusedClass] ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={
                        focusedClass === "First"
                          ? seatData.moreFirstClass
                          : focusedClass === "Business"
                            ? seatData.moreBusinessClass
                            : focusedClass === "Eco+"
                              ? seatData.moreEcoPlus
                              : seatData.moreEcoClass
                      }
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        const field =
                          focusedClass === "First"
                            ? "moreFirstClass"
                            : focusedClass === "Business"
                              ? "moreBusinessClass"
                              : focusedClass === "Eco+"
                                ? "moreEcoPlus"
                                : "moreEcoClass";
                        handleInputChange(field, isChecked);
                      }}
                      disabled={
                        noClassChecked[focusedClass] ||
                        (focusedClass === "First"
                          ? seatData.firstClass === null
                          : focusedClass === "Business"
                            ? seatData.businessClass === null
                            : focusedClass === "Eco+"
                              ? seatData.economyPlusClass === null
                              : seatData.economyClass === null)
                      }
                    />
                    <div className={`${toggleStyle} mt-0.5`}></div>
                    <span className="text-sm text-gray-500">
                      There are more seats available in {focusedClass} class but I don't know how many
                    </span>
                  </label>
                </>
              )}
            </div>

            {/* Comment */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Comment (optional)
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add a comment if needed"
                rows={3}
                className="w-full border border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FBBF24] focus:border-transparent transition-all resize-none"
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-center pb-2">
              <button
                onClick={handleSubmit}
                disabled={
                  seatData.economyClass == null ||
                  seatData.economyClass == 0 ||
                  (!unknownNonRevCount &&
                    (!seatData.nonRevListed || seatData.nonRevListed < 0))
                }
                className={`px-10 py-3 bg-[#FBBF24] hover:bg-[#F59E0B] text-white font-semibold text-sm rounded-full shadow-md transition-all ${
                  seatData.economyClass == null ||
                  seatData.economyClass == 0 ||
                  (!unknownNonRevCount &&
                    (!seatData.nonRevListed || seatData.nonRevListed < 0))
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                Submit Load
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestReplyTab;
